import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { api, apiError } from "../lib/api";
import { waLink } from "../lib/format";
import { TID } from "../lib/testIds";

const inputCls =
  "w-full border border-forest/10 bg-paper px-4 py-3 text-sm text-ink placeholder-ink/30 transition-colors focus:border-forest/60 focus:outline-none";

export const EnquiryForm = ({ packages, maintenanceName, whatsappNumber, defaultPackageId = "" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", package_id: defaultPackageId || "", message: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const options = [
    ...(packages || []).map((p) => ({ value: p.id, label: `${p.name} package` })),
    { value: "maintenance", label: `${maintenanceName || "Maintenance"} plan` },
    { value: "other", label: "Something else" },
  ];
  const selectedName = options.find((o) => o.value === form.package_id)?.label || "working with OFFCRAFT";
  const waText = `Hi OFFCRAFT, I'm interested in ${selectedName}. Please share the next steps.`;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please add your name and a valid email.");
      return;
    }
    setBusy(true);
    try {
      const opt = options.find((o) => o.value === form.package_id);
      await api.post("/enquiries", { ...form, package_name: opt ? opt.label : "" });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid={TID.contact.success} className="border border-emerald-600/30 bg-emerald-600/5 p-8">
        <h3 className="font-display text-2xl font-bold text-ink">Enquiry received.</h3>
        <p className="mt-2 text-sm text-ink/60">We have your details and will reply soon. For a faster start, continue on WhatsApp.</p>
        <a
          data-testid={TID.contact.whatsapp}
          href={waLink(whatsappNumber, waText)}
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-flex items-center gap-2 bg-emerald-500 px-6 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-emerald-400"
        >
          <MessageCircle className="h-4 w-4" /> Continue on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form data-testid={TID.contact.form} onSubmit={submit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <input data-testid={TID.contact.name} className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} />
        <input data-testid={TID.contact.email} className={inputCls} placeholder="Email *" type="email" value={form.email} onChange={set("email")} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input data-testid={TID.contact.phone} className={inputCls} placeholder="Phone / WhatsApp (optional)" value={form.phone} onChange={set("phone")} />
        <select data-testid={TID.contact.select} className={`${inputCls} appearance-none`} value={form.package_id} onChange={set("package_id")}>
          <option value="" className="bg-paper">
            I'm interested in…
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value} className="bg-paper">
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <textarea data-testid={TID.contact.message} rows={4} className={inputCls} placeholder="Tell us about your project" value={form.message} onChange={set("message")} />
      <div className="flex flex-col gap-3 pt-2 sm:flex-row">
        <button
          data-testid={TID.contact.submit}
          disabled={busy}
          type="submit"
          className="inline-flex flex-1 items-center justify-center bg-crimson px-6 py-4 font-display text-sm font-bold text-white transition-colors hover:bg-crimsondark disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send enquiry"}
        </button>
        <a
          data-testid={TID.contact.whatsapp}
          href={waLink(whatsappNumber, waText)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex flex-1 items-center justify-center gap-2 border border-ink/10 px-6 py-4 font-display text-sm font-bold text-ink transition-colors hover:border-emerald-600/60 hover:text-emerald-600"
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp instead
        </a>
      </div>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/55">No spam — we reply personally.</p>
    </form>
  );
};
